import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    type GlobalProps,
    classes,
    Slider,
    createClasses,
} from "@bloomberg/media-ui-core";
import {
    endStatsigPerfLog,
    startStatsigPerfLog,
    useLayer,
    useStatsigClient,
} from "@foundation/statsig";

import { ContextualNavLink } from "~types/types";
import { replaceSpacesWithUnderscores } from "~utils/stringUtil";

import styles from "./ContextualNav.module.css";

export interface ContextualNavTitleProps {
    isWeekend?: boolean;
    title: string;
    link: string;
    onContextualNavClick: (name: string) => void;
}

export interface ContextualNavItemProps {
    isWeekend?: boolean;
    recordIndex: number;
    onContextualNavClick: (name: string) => void;
    contextualNavTitle: string;
    itemTitle: string;
    customUrl?: string | null;
    story?: { id: string; url: string } | null;
}

export type ContextualNavProps = GlobalProps & {
    title: string;
    titleLink?: string;
    slug: string;
    navItems: ContextualNavLink[];
    currentStoryId?: string;
    trackingFunction?: (eventAction: string, eventLabel: string) => void;
};

const MAX_LINKS = 6;

const ContextualNavTitle = ({
    isWeekend,
    title,
    link,
    onContextualNavClick,
}: ContextualNavTitleProps) => {
    const urlWithTracking = `${link}?itm_source=${
        isWeekend ? "weekend_record" : "record"
    }&itm_campaign=${replaceSpacesWithUnderscores(title)}&itm_content=title`;

    return (
        <a
            href={urlWithTracking}
            className={styles.titleLink}
            onClick={() => onContextualNavClick(title)}
        >
            {title}:
        </a>
    );
};

export const ContextualNavItem = ({
    contextualNavTitle,
    isWeekend,
    onContextualNavClick,
    recordIndex,
    itemTitle,
    story,
    customUrl,
}: ContextualNavItemProps) => {
    if (customUrl) {
        const customUrlWithTracking = `${customUrl}?itm_source=${
            isWeekend ? "weekend_record" : "record"
        }&itm_campaign=${replaceSpacesWithUnderscores(
            contextualNavTitle
        )}&itm_content=${replaceSpacesWithUnderscores(
            itemTitle
        )}-${recordIndex}`;

        return (
            <a
                data-component="contextual-nav-item"
                href={customUrlWithTracking}
                key={recordIndex}
                onClick={() => onContextualNavClick(contextualNavTitle)}
            >
                {itemTitle}
            </a>
        );
    }

    const urlWithTracking = `${story?.url}?itm_source=${
        isWeekend ? "weekend_record" : "record"
    }&itm_campaign=${replaceSpacesWithUnderscores(
        contextualNavTitle
    )}&itm_content=${replaceSpacesWithUnderscores(itemTitle)}-${recordIndex}`;

    return (
        <a
            data-component="contextual-nav-item"
            href={urlWithTracking}
            key={recordIndex}
            onClick={() => onContextualNavClick(contextualNavTitle)}
        >
            {itemTitle}
        </a>
    );
};

export const ContextualNav = (props: ContextualNavProps) => {
    const [developingStoriesEnabled, setDevelopingStoriesEnabled] =
        useState(false);

    const {
        title,
        titleLink,
        slug,
        navItems,
        currentStoryId,
        trackingFunction,
    } = props;

    const [hideArrowButtons, setHideArrowButtons] = useState(false);
    const cx = classes(
        [styles.contextualNav, hideArrowButtons && styles.hideArrowButtons],
        props
    );
    const navTitle = `${title}:`;
    const renderedNavItems = navItems.slice(0, MAX_LINKS);

    const statsig = useStatsigClient();
    const developingStoriesLayer = useLayer("developing-stories");

    useEffect(() => {
        if (statsig.client.loadingStatus !== "Ready") {
            return;
        }

        startStatsigPerfLog("SS-developingStoriesLayerGet");
        const enabled = developingStoriesLayer.get("enabled", "false");
        endStatsigPerfLog("SS-developingStoriesLayerGet");

        setDevelopingStoriesEnabled(enabled === "true");
    }, [statsig.client.loadingStatus]);

    const contextualNavClick = useCallback(
        (name: string) => {
            if (trackingFunction) {
                trackingFunction(
                    "click",
                    `record_${replaceSpacesWithUnderscores(name)}`
                );
            }
        },
        [trackingFunction]
    );

    const arrowButtonsRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (
            arrowButtonsRef.current &&
            arrowButtonsRef.current.clientWidth ===
                arrowButtonsRef.current.scrollWidth
        ) {
            setHideArrowButtons(true);
        }
    }, []);

    if (!renderedNavItems || renderedNavItems.length < 1) return null;

    return (
        <div className={cx}>
            <Slider
                className={styles.slider}
                scrollSize={300}
                shadow={false}
                trackRef={arrowButtonsRef}
            >
                {developingStoriesEnabled && (
                    <ContextualNavTitle
                        title={title}
                        link={titleLink || `/latest/${slug}`}
                        onContextualNavClick={contextualNavClick}
                    />
                )}
                {!developingStoriesEnabled &&
                    (titleLink ? (
                        <ContextualNavTitle
                            title={title}
                            link={titleLink}
                            onContextualNavClick={contextualNavClick}
                        />
                    ) : (
                        <p className={styles.title}>{navTitle}</p>
                    ))}
                {renderedNavItems.map((contextualItem, index) => {
                    const isCurrentHeadline =
                        contextualItem.story?.id === currentStoryId;
                    const containerStyles = createClasses([
                        styles.contextualNavItemContainer,
                        {
                            [styles.currentHeadline]: isCurrentHeadline,
                        },
                    ]);

                    return (
                        <div className={containerStyles} key={index}>
                            <ContextualNavItem
                                contextualNavTitle={title}
                                {...contextualItem}
                                recordIndex={index}
                                onContextualNavClick={contextualNavClick}
                                itemTitle={contextualItem.title}
                            />
                        </div>
                    );
                })}
            </Slider>
        </div>
    );
};
